import * as React from 'react'
import { Link as RouterLink } from 'react-router-dom'

import { styled } from 'next-yak'

import { formatDistanceToNow } from '@owl-nest/date'
import { useInterval, SocketEventType, type SocketEvent, type SocketReturn } from '@owl-nest/hooks'
import { t, tn } from '@owl-nest/localize'

import { BREAKPOINTS, Link, glyphs, styles as S } from '@ulule/owl-kit-components/next'

import * as UFE from '../../../../../UFE'
import { useNavigation } from '../../../../hooks/useNavigation'
import { PATHS } from '../../../../paths'

type LiveEventsProps = {
  socket: SocketReturn
}

export function LiveEvents({ socket }: LiveEventsProps): React.ReactElement<any> {
  const navigation = useNavigation()

  const [indexToShow, setIndexToShow] = React.useState<number>(0)
  const [fade, setFade] = React.useState(false)
  const indexEventKeys: SocketEventType | undefined = socket.events?.events
    ? (Object.keys(socket.events.events)[indexToShow] as SocketEventType)
    : undefined

  useInterval(changeEventShowed, 5000)

  React.useEffect(() => {
    setTimeout(function () {
      setFade(false)
    }, 300)
  }, [indexToShow])

  if (indexEventKeys !== undefined) {
    const eventToTemplate = socket.events?.events[indexEventKeys]
    const templatedEvent = eventToTemplate && templateEvent(eventToTemplate)
    return <LiveNews $fade={fade}>{UFE.FLAGS.liveVisitorsCountDisplay && <>{templatedEvent}</>}</LiveNews>
  } else {
    return <></>
  }

  function changeEventShowed(): void {
    if (socket.events?.events && Object.keys(socket.events.events).length > 1) {
      setFade(true)

      setTimeout(function () {
        const nbEvent = socket.events ? Object.keys(socket.events.events).length - 1 : 0
        if (indexToShow < nbEvent) setIndexToShow(indexToShow + 1)
        else setIndexToShow(0)
      }, 300)
    } else setIndexToShow(0)
  }

  function templateEvent(event: SocketEvent): React.JSX.Element | undefined {
    let templatedEvent
    const lastEventIndex = Array.isArray(event?.data) ? event?.data?.length - 1 : 0

    switch (event?.event_type) {
      case SocketEventType.NEWS_PUBLISHED:
        // show if piece of news was published less than 24 hours ago
        templatedEvent = (
          <S.heading.XXXXXS>
            <Link href={event?.data[lastEventIndex]?.absolute_url}>
              <glyphs.twoToned.PaperPlane size={22} />
              {t('The project team posted a post today')}
            </Link>
          </S.heading.XXXXXS>
        )

        break

      case SocketEventType.ORDER:
        // Show if new backers during session or less than 30min ago
        templatedEvent = (
          <S.heading.XXXXXS>
            <Link as={RouterLink} to={navigation.pathTo(PATHS.REWARDS)}>
              <glyphs.twoToned.Owl size={22} />
              {t('%(backer)s backed this project %(time)s ago', {
                backer: event?.data[lastEventIndex]?.user?.name,
                time: formatDistanceToNow(new Date(event?.data[lastEventIndex]?.created_at)),
              })}
            </Link>
          </S.heading.XXXXXS>
        )

        break

      case SocketEventType.BACKERS:
        // Number of backers during last 24 hours
        templatedEvent = (
          <S.heading.XXXXXS>
            <glyphs.twoToned.Owl size={22} />
            {t('%(count)d people backed the project today', { count: event?.data?.count })}
          </S.heading.XXXXXS>
        )

        break

      case SocketEventType.VISITOR:
        templatedEvent = (
          // Current visitor count
          <S.heading.XXXXXS>
            <glyphs.twoToned.Binoculars size={22} />
            {tn(
              '%(count)d visitor currently viewing this page',
              '%(count)d visitors currently viewing this page',
              event?.data?.count,
              {
                count: event?.data?.count,
              },
            )}
          </S.heading.XXXXXS>
        )

        break

      default:
        break
    }
    return templatedEvent
  }
}

const LiveNews = styled.div<{ $fade: boolean }>`
  opacity: ${({ $fade }) => ($fade ? '0' : '1')};
  transition: 0.3s ease-in-out;

  ${glyphs.Icon} {
    margin-right: 10px;
  }

  ${Link} {
    text-decoration: none;
    color: inherit;
  }

  @media screen and ${BREAKPOINTS.TABLET} {
    text-align: right;
  }
`
